import { Box, Card, CardActionArea, Typography } from '@mui/material';
import { RoomOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Purchase } from '../types/purchase.types';
import { StatusBadge } from '@/shared/components/StatusBadge';
import { PLACEHOLDER_IMAGES } from '@/shared/constants/placeholders';

interface PurchaseCardProps {
  purchase: Purchase;
  testId?: string;
}

export function PurchaseCard({ purchase, testId }: PurchaseCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const firstProduct = purchase.offer?.products[0];
  const firstPhoto = firstProduct?.photos[0];
  const title =
    purchase.offer?.title ||
    firstProduct?.material?.subcategory ||
    t('home.purchases.untitledOffer');
  const productStatus = firstProduct?.status;
  const unit = firstProduct?.unit;

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getUnitLabel = (u: { labelFr: string; labelEn: string }): string => {
    return i18n.language === 'fr' ? u.labelFr : u.labelEn;
  };

  return (
    <Card
      sx={{ borderRadius: 2, boxShadow: 1 }}
      data-testid={testId ?? `purchase-card-${purchase.id}`}
    >
      <CardActionArea
        onClick={() => navigate(`/offers/${purchase.offerId}`, { state: { from: 'purchases' } })}
      >
        <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
          {/* Left: product thumbnail */}
          <Box
            sx={{
              width: 90,
              height: 90,
              flexShrink: 0,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={firstPhoto?.thumbnailUrl || firstPhoto?.url || PLACEHOLDER_IMAGES.product}
              alt={title}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.src = PLACEHOLDER_IMAGES.product;
              }}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>

          {/* Right: details */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            {/* Title */}
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {title}
            </Typography>

            {/* Product name */}
            {firstProduct?.material?.subcategory && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {firstProduct.material.subcategory}
              </Typography>
            )}

            {/* Location */}
            {purchase.offer?.address && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RoomOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {purchase.offer.address}
                </Typography>
              </Box>
            )}

            {/* Quantity */}
            {firstProduct && unit && (
              <Typography variant="body2" color="text.secondary">
                {t('home.purchases.quantity', {
                  value: firstProduct.quantity,
                  unit: getUnitLabel(unit),
                })}
              </Typography>
            )}

            {/* Status badge */}
            {productStatus && (
              <Box sx={{ mt: 0.5 }}>
                <StatusBadge status={productStatus} size="small" />
              </Box>
            )}

            {/* Purchase date */}
            <Typography variant="caption" color="text.secondary">
              {t('home.purchases.purchasedOn', { date: formatDate(purchase.purchaseDate) })}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}
